import React from 'react';
import { useChatBar } from '../../context/ChatBarContext';
import ChatWindow from './ChatWindow';
import { X, MessageCircle } from 'lucide-react';

const ChatBar: React.FC = () => {
  const { openChats, closeChat, toggleMinimize } = useChatBar();

  if (openChats.length === 0) {
    return null;
  }

  const totalUnread = openChats.reduce((sum, chat) => sum + chat.unreadCount, 0);

  return (
    <>
      {/* Expanded Chat Windows - Pop up above the bar */}
      <div className="fixed bottom-12 right-4 z-[9999] flex items-end gap-2 pointer-events-none">
        {openChats
          .filter(chat => !chat.isMinimized)
          .map((chat) => (
            <div key={chat.chatId} className="pointer-events-auto">
              <ChatWindow chat={chat} />
            </div>
          ))}
      </div>

      {/* Sticky Footer Bar - Light Mode */}
      <div className="fixed bottom-0 left-0 right-0 z-[9998] h-12 bg-white border-t border-gray-200 shadow-lg pointer-events-auto">
        <div className="max-w-screen-2xl mx-auto h-full px-4 flex items-center justify-between gap-3">
          {/* Left side - Chat Bubbles */}
          <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {openChats.map((chat) => {
              const displayName = chat.chatData?.displayName || chat.chatData?.name || 'Chat';
              const photoURL = chat.chatData?.photoURL || null;
              const isGroup = chat.chatData?.isGroup || false;

              return (
                <button
                  key={chat.chatId}
                  onClick={() => toggleMinimize(chat.chatId)}
                  className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                    chat.isMinimized
                      ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300'
                      : 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-200'
                  }`}
                  title={chat.isMinimized ? 'Click to expand' : 'Currently expanded'}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {isGroup ? (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center border border-gray-200">
                        <MessageCircle className="w-3 h-3 text-indigo-600" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-200">
                        {photoURL ? (
                          <img
                            src={photoURL}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                              if (nextElement) {
                                nextElement.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-full h-full bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs ${
                            photoURL ? 'hidden' : 'flex'
                          }`}
                        >
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                      </div>
                    )}
                    
                    {/* Unread badge */}
                    {chat.unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1 animate-pulse">
                        {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <span className="text-xs text-gray-700 font-medium max-w-[120px] truncate">
                    {displayName}
                  </span>

                  {/* Individual close button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeChat(chat.chatId);
                    }}
                    className="ml-1 p-0.5 hover:bg-red-100 rounded transition-colors text-gray-400 hover:text-red-500"
                    title="Close this chat"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </button>
              );
            })}
          </div>

          {/* Right side - Close All button */}
          <button
            onClick={() => {
              // Close all chats (localStorage will be updated automatically)
              openChats.forEach(chat => closeChat(chat.chatId));
            }}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 rounded-lg transition-all text-red-600 hover:text-red-700"
            title="Close all chats"
          >
            <span className="text-xs font-medium hidden sm:inline">Close All</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatBar;

